import axios from 'axios';
import { addQueryParamsWithoutReload } from 'modules/main/utils';
import { ISubscribe } from 'modules/shop/newsletter/types';
import { AppRoutes } from 'modules/shop/routes';
import {
  getFactFinderProducts,
  getFactFinderProductsFallback,
  getLongTailProducts,
} from 'commerce/factFinder/api';
import { SortOptions, SortOrder } from 'commerce/factFinder/queryParameters';
import { Facets, GetFFProductsParams } from 'commerce/factFinder/types';
import { errorWrapper } from 'commerce/utils/network';
import { EMARSYS_SHOP_ID } from 'src/envVariables';
import { adaptProducts } from './common';

const AAA_ENDPOINTS = {
  /** This is proxy through our server. See next.config.js.
   * Api details - https://confluence.internetsupport-bielefeld.de/x/RAATAw
   */
  EMARSYS: `/api/aaa/emarsys-data-sync/v1`,
};

export const getCategoryProductsClient = async (
  params: GetFFProductsParams,
) => {
  const { response: ffResp } = await getFactFinderProducts(
    params,
    (urlParams) => {
      addQueryParamsWithoutReload(urlParams);
    },
  );

  return adaptProducts(ffResp);
};

export const getLongTailProductsClient = async (
  params: GetFFProductsParams,
) => {
  const { response } = await getLongTailProducts(params);
  return adaptProducts(response);
};

export const getSearchProductsClient = async (params: GetFFProductsParams) => {
  let { response } = await getFactFinderProducts(params, (query) => {
    addQueryParamsWithoutReload(query);
  });

  if (!response?.data?.hits?.length) {
    ({ response } = await getFactFinderProductsFallback(params));
  }

  return adaptProducts(response);
};

export const getPreSearchProductsClient = async (query: string) => {
  const params = {
    filters: { [Facets.TITLE]: query },
    query,
    sortBy: { field: SortOptions.relevancy, direction: SortOrder.desc },
    page: 1,
    perPage: 10,
  } as GetFFProductsParams;

  let { response } = await getFactFinderProducts(params);

  if (!response?.data?.hits?.length) {
    ({ response } = await getFactFinderProductsFallback(params));
  }

  return adaptProducts(response);
};

export const subscribeToNewsLetter = async (
  email: string,
  locale: string,
): Promise<ISubscribe> => {
  const initUrl = `${AAA_ENDPOINTS.EMARSYS}/newsletters/opt_in/init`;

  // {nlOptInConfirmationUrl} cannot contain a query string, so we need to encode the email and send it as part of url
  const encodedEmail = btoa(email);
  const data = {
    shopIdentifier: EMARSYS_SHOP_ID,
    emailAddress: email,
    nlOptInConfirmationUrl: `${window.location.origin}${AppRoutes.NEWSLETTER_CONFIRMED}/${encodedEmail}`,
    shopLanguage: locale,
  };

  return await errorWrapper(subscribeToNewsLetter.name, () =>
    axios.post(initUrl, data),
  );
};

export const subscribeToNewsLetterComplete = (email: string) => {
  const completeUrl = `${AAA_ENDPOINTS.EMARSYS}/newsletters/opt_in/complete`;

  return axios.post(completeUrl, {
    emailAddress: email,
    shopIdentifier: EMARSYS_SHOP_ID,
  });
};

export const unsubscribeFromNewsLetter = (email: string) => {
  const url = `${AAA_ENDPOINTS.EMARSYS}/newsletters/opt_out`;
  return axios.post(url, {
    emailAddress: email,
    shopIdentifier: EMARSYS_SHOP_ID,
  });
};

export const checkSubscription = (email: string) => {
  const url = `${AAA_ENDPOINTS.EMARSYS}/contacts/get_uid`;
  return axios.post(url, {
    emarsysContactEmail: email,
    shopIdentifier: EMARSYS_SHOP_ID,
  });
};

export const subscribeToUWG7 = (email: string, locale: string) => {
  const initUrl = `${AAA_ENDPOINTS.EMARSYS}/uwg7/opt_in`;

  const data = {
    shopIdentifier: EMARSYS_SHOP_ID,
    emailAddress: email,
    shopLanguage: locale,
  };

  return axios.post(initUrl, data);
};
